import React from 'react';
import { 
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  BanknotesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { SeekersPipelineProps } from './types';
import { generateAvatar, getStageTitle, formatCurrency } from './utils';

const SeekersPipelineList: React.FC<SeekersPipelineProps> = ({ seekers, onSeekerClick }) => {
  const getStatusBadgeColor = () => 'bg-[#2C2731] text-[#FFFCEF] border border-[#FFFCEF]';

  const getStageIcon = (stage: string, notes?: string) => {
    switch (stage) {
      case 'matched':
        return <UserIcon className="w-4 h-4" />;
      case 'pending_consultation':
        return notes === 'Scheduled' ? <CheckCircleIcon className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />;
      case 'awaiting_package':
        return <UserIcon className="w-4 h-4" />;
      case 'payment_pending':
        return <BanknotesIcon className="w-4 h-4" />;
      case 'active_clients':
        return <CheckCircleSolidIcon className="w-4 h-4" />;
      default:
        return <UserIcon className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden mb-8 text-[#2C2731]">
      <div className="px-6 py-4 border-b border-[#2C2731] bg-[#2C2731]">
        <h2 className="text-base font-medium text-[#FFFCEF] font-recoleta">All Seekers</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#2C2731]">
          <thead className="bg-[#E8DFBD]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#2C2731] uppercase tracking-wider">
                Seeker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#2C2731] uppercase tracking-wider">
                Stage
              </th>
              {/*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Activity
              </th>*/}
              <th className="px-6 py-3 text-left text-xs font-medium text-[#2C2731] uppercase tracking-wider">
                Notes
              </th>
              {/*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>*/}
              <th className="px-6 py-3 text-right text-xs font-medium text-[#2C2731] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#FFFCEF] divide-y divide-[#2C2731]/40">
            {seekers.map((seeker) => (
              <tr 
                key={seeker.id} 
                className="hover:bg-[#F3EBCD] cursor-pointer transition-colors"
                onClick={() => onSeekerClick(seeker)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {generateAvatar(seeker.name)}
                    <div className="ml-4">
                      <div className="text-sm font-medium text-[#2C2731] ph-mask">{seeker.name}</div>
                      <div className="text-sm text-[#2C2731]/70">{seeker.title}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor()}`}>
                      {getStageIcon(seeker.stage, seeker.notes)}
                      <span className="ml-1">{getStageTitle(seeker.stage)}</span>
                    </span>
                  </div>
                </td>
                {/*<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {seeker.lastActivity}
                </td>*/}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#2C2731]/80">
                  {seeker.notes 
                    ? seeker.notes.length > 30 
                      ? seeker.notes.slice(0, 30) + '...' 
                      : seeker.notes 
                    : '-'}
                </td>
                {/*<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {seeker.amount ? formatCurrency(seeker.amount) : '-'}
                </td>*/}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onSeekerClick(seeker);
                      }}
                      className="text-[#2C2731] hover:text-[#241F2A]"
                      title="View Details"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#2C2731]/60 hover:text-[#2C2731]"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#2C2731]/60 hover:text-[#2C2731]"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeekersPipelineList;
